import BanWordsForm from '@/components/pages/create-webinar/live/BanWordsForm';
import ChatConfigurationSection from '@/components/pages/create-webinar/evergreen/ChatConfigurationSection';
import ListMessages from '@/components/pages/create-webinar/evergreen/ListMessages';
import MessageCreate from '@/components/pages/create-webinar/evergreen/MessageCreate';
import TitleLayout from '@/components/pages/create-webinar/TitleLayout';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { fetchEvergreenChatConfiguration } from '@/features/webinar/webinarThunks';
import type { EvergreenTimelineEvent } from '@/features/webinar/types';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ViewerStatus from '@/components/pages/create-webinar/live/status/ViewerStatus';
import { Button } from '@/components/ui/button';

interface ChatEvergreenSectionProps {
  currentWebinarId?: number | null;
  isEdit?: boolean;
  onSaveSuccess?: () => void;
}
export default function ChatEvergreenSection({
  currentWebinarId,
  isEdit,
  onSaveSuccess,
}: ChatEvergreenSectionProps) {
  const { t } = useTranslation('createWebinar');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const workspaceId = useAppSelector((state: RootState) => state.workspace.currentWorkspace?.id);

  const currentWebinarSession = useAppSelector(
    (state: RootState) => state.webinar.currentWebinarSession,
  );
  const { webinarId: webinarIdParam } = useParams<{ webinarId?: string }>();
  const webinarIdRaw = webinarIdParam || currentWebinarId;
  const webinarId =
    typeof webinarIdRaw === 'number' ? webinarIdRaw : parseInt(webinarIdRaw ?? '0', 10);

  const [accordionConfigValue, setAccordionConfigValue] = useState('');
  const [openMessagesTrigger, setOpenMessagesTrigger] = useState(0);
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const chatMessages = useAppSelector(
    (state: RootState) => state.webinar.currentEvergreenChatConfiguration?.messages,
  );
  const editingMessage =
    editingMessageId == null
      ? null
      : (chatMessages?.find((message) => message.id === editingMessageId) ?? null);

  useEffect(() => {
    if (!workspaceId || !webinarId || isNaN(webinarId)) return;
    dispatch(fetchEvergreenChatConfiguration({ workspaceId, webinarId }));
  }, [workspaceId, webinarId, dispatch]);

  const isEvergreenSessionReadOnly =
    currentWebinarSession != null &&
    currentWebinarSession.webinarId === webinarId &&
    currentWebinarSession.sessionType === 'EVERGREEN' &&
    currentWebinarSession.status === 'IN_PROGRESS';

  const handleConfigSaveSuccess = () => {
    setAccordionConfigValue('');
    setOpenMessagesTrigger((t) => t + 1);
  };

  const handleViewerStatusSaveSuccess = () => {
    setAccordionConfigValue('item-1');
  };

  const handleEditMessage = (message: EvergreenTimelineEvent) => {
    setEditingMessageId(message.id);
  };

  const handleEditEnd = () => {
    setEditingMessageId(null);
    setOpenMessagesTrigger((t) => t + 1);
  };

  const handleValider = () => {
    if (isEdit) {
      onSaveSuccess?.();
      return;
    }
    if (webinarId && !isNaN(webinarId)) {
      navigate(`/create-webinar/post-event/${webinarId}`);
    } else {
      navigate('/create-webinar/post-event');
    }
  };

  return (
    <TitleLayout
      title={t('chatEvergreenSection.title')}
      isEdit={isEdit}
      subtitle={t('chatEvergreenSection.subtitle')}
    >
      <div className="space-y-8">
        <ViewerStatus
          webinarId={webinarId}
          workspaceId={workspaceId ?? 0}
          isEvergreen
          isEdit={isEdit}
          readOnly={isEvergreenSessionReadOnly}
          onThresholdSaved={handleViewerStatusSaveSuccess}
        />
        <ChatConfigurationSection
          accordionValue={accordionConfigValue}
          onAccordionValueChange={setAccordionConfigValue}
          onSaveSuccess={handleConfigSaveSuccess}
          isEdit={isEdit}
          readOnly={isEvergreenSessionReadOnly}
        />
        <BanWordsForm isEdit={isEdit} />
        <MessageCreate
          key={editingMessage?.id ?? 'new'}
          openTrigger={openMessagesTrigger}
          isEdit={isEdit}
          readOnly={isEvergreenSessionReadOnly}
          editingMessage={editingMessage}
          onEditEnd={handleEditEnd}
        />
        <ListMessages
          readOnly={isEvergreenSessionReadOnly}
          editingMessageId={editingMessage?.id ?? null}
          onEditMessage={handleEditMessage}
        />

        <div className="w-full">
          <Button type="button" className="w-full" onClick={handleValider}>
            {t('chatEvergreenSection.validate')}
          </Button>
        </div>
      </div>
    </TitleLayout>
  );
}
